import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './CaseStudiesWidget.scss'
import LogoMaster from '../assets/images/logomaster.png'
import OutdatedWebsite from '../assets/images/healthcare-casestudy.jpg'
import LegacySystems from '../assets/images/upgrading-legacy-systems.jpg'
import MigratingCloud from '../assets/images/migrating-cloud.jpg'

const CaseStudiesWidget = ({ bckColorClass = '' }) => {
  const caseStudiesData = [
    {
      title: 'Redesigning an outdated website',
      description: 'A leading healthcare provider needed to redesign their website to create a more user-friendly and accessible platform for patients. The new website enhanced the UX and resulted in a 38% increase in online engagement.',
      client: 'Leading healthcare provider',
      industry: 'Healthcare',
      imageUrl: OutdatedWebsite,
      logoUrl: LogoMaster,
      sections: [
        {
          title: 'The Challenge',
          description:
            'The client had an outdated website. It lacked a modern design and clear navigation, leading to high bounce rates. The online booking process was complicated and the site wasn’t optimised for mobile devices.'
        },
        {
          title: '',
          description: '<br>The main objectives were to:',
          points: [
            'Redesign the website for an improved user experience',
            'Make the website mobile-friendly and responsive',
            'Streamline the appointment booking process'
          ]
        },
        {
          title: 'Our Solution',
          description:
            'We worked collaboratively with the client to understand their specific needs and challenges.'
        },
        {
          title: '',
          description: '<br>Our solution included:',
          points: [
            'A full website redesign with simplified navigation',
            'Mobile optimisation for an improved experience on smartphones',
            'Providing a streamlined appointment booking process'
          ]
        },
        {
          title: 'Results',
          description: 'The new website resulted in:',
          points: [
            'A 38% increase in online appointment bookings',
            'A 26% reduction in bounce rates',
            'Improved patient satisfaction following a patient survey'
          ]
        }
      ]
    },
    {
      title: 'Upgrading legacy systems',
      description: 'A law firm wanted to update its outdated system for case management and client communication. We developed a custom solution that resulted in a 30% increase in staff productivity.',
      client: 'Law firm',
      industry: 'Legal',
      imageUrl: LegacySystems,
      logoUrl: LogoMaster,
      sections: [
        {
          title: 'The Challenge',
          description:
            'The client was using an outdated system that was difficult to maintain. They used multiple systems and wanted to integrate them to improve efficiency and the customer experience.'
        },
        {
          title: '',
          description: '<br>The main objectives were to:',
          points: [
            'Modernise and upgrade the legacy systems',
            'Improve integration between systems',
            'Ensure the system is secure, scalable, and compliant'
          ]
        },
        {
          title: 'Our Solution',
          description:
            'We worked closely with the client to evaluate their needs and propose a custom solution.'
        },
        {
          title: '',
          description: '<br>Our solution included:',
          points: [
            'Conducting a thorough audit of the existing systems',
            'Designing and implementing an integrated case system',
            'Migrating the legacy data to the cloud for improved security'
          ]
        },
        {
          title: 'Results',
          description: 'After the upgrade, the firm experienced significant improvements including:',
          points: [
            '30% increase in staff productivity',
            '8% reduction in operational costs',
            'Improved client communication'
          ]
        }
      ]
    },
    {
      title: 'Migrating to the cloud',
      description: 'An insurance firm wanted to migrate its onsite IT infrastructure to the cloud. They wanted to reduce operational costs and increase data security. By migrating to the cloud, the client reduced operational costs by 18%.',
      client: 'Insurance firm',
      industry: 'Financial Services',
      imageUrl: MigratingCloud,
      logoUrl: LogoMaster,
      sections: [
        {
          title: 'The Challenge',
          description:
            'The client wanted to upgrade its infrastructure by migrating to the cloud. Maintenance of their onsite system was inefficient and expensive. They wanted to increase their data security and maintain compliance with industry regulations.'
        },
        {
          title: '',
          description: '<br>The main objectives were to:',
          points: [
            'Migrate the IT infrastructure to the cloud',
            'Improve scalability to handle business growth',
            'Ensure data security and regulatory compliance'
          ]
        },
        {
          title: 'Our Solution',
          description:
            'We worked closely with the client to plan and execute a seamless migration to the cloud.'
        },
        {
          title: '',
          description: '<br>Our solution included:',
          points: [
            'Selecting the cloud platform that suits their business needs',
            'Developing and executing a seamless migration plan',
            'Implementing security protocols to meet industry standards'
          ]
        },
        {
          title: 'Results',
          description: 'The migration to the cloud led to:',
          points: [
            '18% reduction in operational costs',
            'Enhanced data security to meet industry standards',
            'Increased system uptime and improved reliability'
          ]
        }
      ]
    }
  ]
  const [prevCaseStudyIndex, setPrevCaseStudyIndex] = useState(0)

  const navigate = useNavigate()
  const refCaseStudyItem = useRef(new Array())
  let prevExpanded = ''

  const openOverlay = (i) => {
    refCaseStudyItem.current[prevCaseStudyIndex].classList.remove('expanded')
    refCaseStudyItem.current[i].classList.add('expanded')
    setPrevCaseStudyIndex(i)
  }
  const closeOverlay = (i) => {
    refCaseStudyItem.current[i].classList.remove('expanded')
  }

  return (
    <section className={`case-studies-widget ${bckColorClass}`}>
      <div className="container">

        <div className="case-studies-grid">
          {caseStudiesData.map((caseStudy, index) => (
            <div ref={(element) => refCaseStudyItem.current[index] = element} className='case-study-card position-relative fade-in-delayed-3s' style={{ animationDelay: index * 200 + 'ms' }}
                 key={index}>
              <div className="case-study-image" style={{ backgroundImage: `url(${caseStudy.imageUrl})` }}></div>
              <div className="case-study-content">
                <img src={caseStudy.logoUrl} alt={`${caseStudy.title} logo`} className="case-study-logo" />
                <h3 className="case-study-title">{caseStudy.title}</h3>
                <p className="case-study-subtitle">{caseStudy.description}</p>
                <div className="case-study-meta">
                  <strong>Client:</strong> {caseStudy.client} <br />
                  <strong>Industry:</strong> {caseStudy.industry}
                </div>
                <div className="button-holder">
                  <button className="button" onClick={() => {
                    openOverlay(index)
                  }}>
                    Read Case Study
                  </button>
                </div>
              </div>
              <div className="case-study-overlay">
                <div className="case-study-close close" onClick={() => {
                  closeOverlay(index)
                }}>
                  <span className="top"></span>
                  <span className="bottom"></span>
                </div>
                <div className="expanded-text">
                  {caseStudy.sections.map((section, i) => (
                    <div key={i}>
                      {section.title && <h4 className="challenge-title">{section.title}</h4>}
                      {section.description && <p dangerouslySetInnerHTML={{ __html: section.description }}></p>}
                      {section.points && (
                        <ul>
                          {section.points.map((point, index) => (
                            <li key={index}>{point}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
                <button className="button talk-to-us" onClick={() => navigate('/contact')}>
                  Talk To Us About Your Project Today
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default CaseStudiesWidget